<template>
    <v-container v-if="loading" fill-height>
        <v-layout row align-center justify-center fill-height>
            <v-flex shrink>
                <v-progress-circular indeterminate></v-progress-circular>
            </v-flex>
        </v-layout>
    </v-container>
    <v-container v-else class="pa-2 flex-row fullWidth fullHeight" fluid>
        <v-card v-if="error != null">
            <v-layout wrap>
                <v-flex xs12>
                    <v-alert :value="error != null" outline type="error">
                        {{error}}
                    </v-alert>
                </v-flex>
            </v-layout>
        </v-card>
        <v-card v-else class="flex-grow-1 d-flex flex-column fullWidth fullHeight">
            <v-toolbar card color="primary subheading" dark height="64" class="flex-grow-0 d-flex" >
                <v-layout row align-start justify-right>
                    <v-flex grow align-self-center>
                        <span class="title">Rostering Management</span>
                    </v-flex>
                   
                    <v-flex grow align-self-center v-if="selectedPlantID != null && selectedCategoryIDs.length > 0" class="subheading font-weight-bold white--text">
                        {{ financialWeekDescription }}  {{ dateRangeDescription }}
                    </v-flex>
                    <v-flex shrink>
                        <RosterOverviewWeather></RosterOverviewWeather>
                    </v-flex>
                    <v-flex shrink align-self-center>
                        <v-btn flat block @click="hideControl = !hideControl" :disabled="selectedPlantID < 0 || selectedCategoryIDs.length == 0 || selectedDate == null">
                            {{hideControl ? 'Show': 'Hide'}} Control
                        </v-btn>
                    </v-flex>
                     <v-flex shrink align-self-center>
                        <v-btn flat icon color="white" @click="refresh" :disabled="selectedPlantID < 0 || selectedCategoryIDs.length == 0 || selectedDate == null">
                                <v-icon>refresh</v-icon>
                        </v-btn>
                    </v-flex>
                </v-layout>
            </v-toolbar>
            <v-container class="pl-1 pr-1 pt-0 pb-0 flex-grow-1 d-flex flex-column" fluid>
                <v-layout row align-start justify-center class="flex-grow-0" v-if="!hideControl">
                    <v-flex>
                        <RosterOverviewFilters
                            :routePlantID="routePlantID"
                            :routeProductCategoryID="routeProductCategoryID"
                            :loading="loading || loadingRoster"
                            @loadCropLocationVarietyRosterData="loadData"
                            @updateTeamRosterPlans="updateTeamRosterPlans">
                        </RosterOverviewFilters>
                    </v-flex>
                </v-layout>
                <v-layout v-if="loadingRoster" row align-center justify-center fill-height>
                    <v-flex shrink>
                        <v-progress-circular indeterminate></v-progress-circular>
                    </v-flex>
                </v-layout>
                <v-layout v-else-if="loadError != null" row align-center justify-center fill-height>
                    <v-flex shrink>
                        <v-alert :value="loadError != null" outline type="error">
                            {{loadError}}
                        </v-alert>
                    </v-flex>
                </v-layout>
                <v-layout v-else row class="d-flex flex-column">
                        <!-- Main Content -->
                        <v-card class="d-flex flex-column" :style="teamTableStyle">
                            <RosterTeamTable @expand="teamTableExpand" /> 
                        </v-card>
                        <v-card class="d-flex flex-column" :style="locationTableStyle">
                            <RosterLocationTable @expand="locationTableExpand" />
                        </v-card>
                </v-layout>
            </v-container>
        </v-card>
    </v-container>
</template>

<script>

import { mapActions, mapState } from 'vuex'
import RosterOverviewWeather from '@/components/berriesRoster/RosterOverviewWeather.vue'
import RosterOverviewFilters from '@/components/berriesRoster/RosterOverviewFilters.vue'
import RosterLocationTable from '@/components/berriesRoster/RosterLocationTable.vue'
import RosterTeamTable from '@/components/berriesRoster/RosterTeamTable.vue'
import RosterUtilService from '@/services/rosterUtilService';
import moment from 'moment';
export default {
    components: {
        RosterOverviewWeather,
        RosterOverviewFilters,
        RosterLocationTable,
        RosterTeamTable
    },
    props: {
        routePlantID: {
            type: Number,
            required: false
        },
        routeProductCategoryID: {
            type: Number,
            required: false
        }
    },
    data(){
        return {
            loading: false,
            loadingRoster: false,
            error: null,
            loadError: null,
            hideControl: false,
            teamTableExpanded: true,
            locationTableExpanded: true
        }
    },
    computed: {
        financialWeekDescription(){
            var financialWeek = RosterUtilService.getFinancialWeek(this.selectedDate, this.financialYearCalendarData);
            return `Active WEEK ${financialWeek}`;
        },
        dateRangeDescription() {
            return `${this.$options.filters.formatDate(moment(this.selectedDate).startOf('week'))} - ${this.$options.filters.formatDate(moment(this.selectedDate).endOf('week'))}`;
        },
        ...mapState("RosterStore", [
            "selectedPlantID",
            "selectedCategoryIDs",
            "selectedDate",
            "financialYearCalendarData"
        ]),
        teamTableStyle() {
            let teamTableMin = "45%";
            let teamTableMax = "100%";

            if (this.teamTableExpanded && !this.locationTableExpanded) {
                teamTableMin = "90%";
                teamTableMax = "100%";
            }

            if (!this.teamTableExpanded) {
                teamTableMin = "78px";
                teamTableMax = "78px";
            }

            return `position:relative; min-height:${teamTableMin}; max-height:${teamTableMax}; width:100%;`;
        },
        locationTableStyle() {
            let locationTableMin = "55%";
            let locationTableMax = "100%";

            if (!this.teamTableExpanded) {
                locationTableMin = "90%";
                locationTableMax = "100%"
            }

            if (!this.locationTableExpanded && this.teamTableExpanded) {
                locationTableMin = "78px";
                locationTableMax = "78px"
            }

            return `position:relative; min-height:${locationTableMin}; max-height:${locationTableMax}; width:100%;`;
        }
    },
    methods: {
        ...mapActions("RosterStore", [
            "loadFinancialYearCalendarData",
            "loadPlants",
            "loadCropLocationVarietyRosterData",
            "loadRosterTeamPlans",
            "updateRosterTeamPlans",
            "resetState",
            "cleanUpRosterTeamPlans",
            "clearLocalTeamIDs"
        ]),
        teamTableExpand(expand) {
            this.teamTableExpanded = expand;
        },
        locationTableExpand(expand) {
            this.locationTableExpanded = expand;
        },
        async init(){
            try {
                this.loading = true;
                this.error = null;
                await Promise.all([
                    this.resetState(),
                    this.loadFinancialYearCalendarData(),
                    this.loadPlants()
                ]);
            } catch(e) {
                this.error = e.toString();
            } finally {
                this.loading = false;
            }
        },
        async refresh() {
            this.loadingRoster = true;
            await this.loadData();
            this.loadingRoster = false;
        },
        async loadData() {
            try {
                this.loadError = null;
                this.loadingRoster = true;
                this.teamTableExpanded = true;
                this.locationTableExpanded = true;
                
                await this.clearLocalTeamIDs();
                await this.cleanUpRosterTeamPlans();
                await Promise.all([
                    this.loadRosterTeamPlans(),
                    this.loadCropLocationVarietyRosterData()
                ]);
            } catch(e) {
                this.loadError = e.toString();
            } finally {
                this.loadingRoster = false;
            }
        },
        async updateTeamRosterPlans(updateRosterTeamPlans) {
             try {
                this.loadError = null;
                this.loadingRoster = true;
                await this.updateRosterTeamPlans(updateRosterTeamPlans);
            } catch(e) {
                this.loadError = e.toString();
            } finally {
                this.loadingRoster = false;
            }
        }
    },
    created(){
        this.init();
    }
}
</script>
<style scoped>
.fullWidth {
    width: 100%;
}
.fullHeight {
    height: 100%;
}
.flex-column {
    flex-direction: column!important;
}

.flex-row {
    flex-direction: row!important;
}

.d-flex {
    display: flex!important;
}

.flex-grow-1 {
    flex-grow: 1!important;
}

.flex-grow-0 {
    flex-grow: 0!important;
}

</style>